import { useState, useEffect, useCallback } from 'react';
import { Project } from '../../../utils/openai-api';
import { OrganizationData, ViewMode } from '../types';
import { debugLog } from '../../../utils/debug-log';

export const useUrlProjectSync = (data: OrganizationData | null) => {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('overview');

  // Get project ID from URL query parameters
  const getProjectIdFromUrl = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('project');
  }, []);

  const updateUrlWithProject = useCallback((projectId: string | null) => {
    const url = new URL(window.location.href);
    if (projectId) {
      url.searchParams.set('project', projectId);
      url.searchParams.set('tab', 'openai');
    } else {
      url.searchParams.delete('project');
    }
    window.history.pushState({}, '', url.toString());
  }, []);

  // Restore project and view from URL on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const view = urlParams.get('view');

    if (view === 'usage') {
      setViewMode('usage');
      return;
    }

    const projectId = getProjectIdFromUrl();
    if (projectId && data) {
      const project = data.projects.find(p => p.id === projectId);
      if (project) {
        setSelectedProject(project);
        setViewMode('project-detail');
      }
    }
  }, [data, getProjectIdFromUrl]);

  // Handle browser back/forward button
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const view = urlParams.get('view');

      if (view === 'usage') {
        setViewMode('usage');
        setSelectedProject(null);
        return;
      }

      const projectId = getProjectIdFromUrl();
      if (projectId && data) {
        const project = data.projects.find(p => p.id === projectId);
        if (project) {
          setSelectedProject(project);
          setViewMode('project-detail');
        } else {
          setSelectedProject(null);
          setViewMode('overview');
        }
      } else {
        setSelectedProject(null);
        setViewMode('overview');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [data, getProjectIdFromUrl]);

  // Project detail view handler
  const handleProjectClick = useCallback((project: Project) => {
    debugLog('Project clicked:', project);
    setSelectedProject(project);
    setViewMode('project-detail');
    updateUrlWithProject(project.id);
  }, [updateUrlWithProject]);

  // Return to overview
  const handleBackToOverview = useCallback(() => {
    setSelectedProject(null);
    setViewMode('overview');
    // Remove project and view while keeping URL parameters
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    // Keep necessary parameters
    const tab = params.get('tab') || 'openai';
    const sortBy = params.get('sortBy');
    const sortDir = params.get('sortDir');

    // Build new parameters
    const newParams = new URLSearchParams();
    newParams.set('tab', tab);
    if (sortBy) newParams.set('sortBy', sortBy);
    if (sortDir) newParams.set('sortDir', sortDir);

    url.search = newParams.toString();
    window.history.pushState({}, '', url.toString());
  }, []);

  // Navigate to usage dashboard
  const handleViewUsage = useCallback(() => {
    setViewMode('usage');
    // Add view while keeping URL parameters
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    // Keep existing parameters
    params.set('tab', 'openai');
    params.set('view', 'usage');
    // Remove project parameter
    params.delete('project');

    url.search = params.toString();
    window.history.pushState({}, '', url.toString());
  }, []);

  return {
    viewMode,
    selectedProject,
    handleProjectClick,
    handleBackToOverview,
    handleViewUsage
  };
};
